import React from 'react'
import { CircularProgress } from '@mui/material'
import { useAppDispatch } from '@SonarCX/hooks/redux'
import { Row } from '@tanstack/react-table'

import SingleSubtask from './SingleSubtask'

import { nullExpandArray } from '@SonarCX/utilities'
import { AddErrorAlert, AddSuccessAlert } from '@SonarCX/slices/alert'
import { useTaskAPI } from '@SonarCX/providers/TaskAPIProvider'
import { useTreeState, TYPES } from '@SonarCX/providers/TreeStateProvider'
import {
  ALL_TAB,
  useTaskPageState
} from '@SonarCX/providers/TaskPageStateProvider'
import useCombinedTableState from '@SonarCX/hooks/useCombinedTableState'
import { useTaskList } from '@SonarCX/providers/TaskListProvider'
import useStyles from './styles'

type SubTaskProps = {
  id: string | null
  row: Row<LocalTask>
  depth?: number
  targetRef: React.MutableRefObject<any>
  smallMedia: boolean
  onMove: (id: string, tasks: APITask[]) => void
  observerId: string
  enableCustomContextMenu?: boolean
  customFieldsToInclude: string
}

const SubTask: React.FC<SubTaskProps> = ({
  id,
  row,
  depth = 1,
  targetRef,
  smallMedia,
  onMove,
  observerId,
  enableCustomContextMenu = false,
  customFieldsToInclude = ''
}) => {
  const dispatch = useAppDispatch()

  const { tasks, getSubtasks, loadTask, patchTasks } = useTaskAPI()
  const { state: treeState } = useTreeState(TYPES.TASK)
  const { includeSubtasks } = useCombinedTableState()
  const { tab } = useTaskPageState()
  const { pushFrame } = useTaskList()

  const [loading, setLoading] = React.useState(false)
  const [subtaskIDs, setSubtaskIDs] = React.useState(null)
  const [expanded, setExpanded] = React.useState<string[]>([])

  const subtasks = React.useMemo(() => {
    if (!Array.isArray(subtaskIDs) || loading) {
      return []
    }

    const subs: LocalTask[] = []
    subtaskIDs.forEach(id => {
      const task = tasks[id]
      if (task) {
        subs.push(task)
      }
    })
    return subs.sort((a, b) => a.sort_order - b.sort_order)
  }, [subtaskIDs, loading, tasks])

  const task = React.useMemo(() => tasks[id], [tasks, id])

  const hideSubs = React.useMemo(
    () => tab === ALL_TAB && includeSubtasks,
    [tab, includeSubtasks]
  )

  const classes = useStyles({ depth })

  // Loads this task
  React.useEffect(() => {
    if (id && !task) {
      setLoading(true)
      loadTask(id)
    } else {
      setLoading(false)
    }
  }, [id, task, loadTask])

  // Sets initial expanded state based off stored tree state
  React.useEffect(() => {
    const expanded = []

    subtasks.forEach(task => {
      const id = task.id
      const isExpanded = id in treeState && Boolean(treeState[id])
      if (isExpanded) {
        expanded.push(id)
      }
    })

    setExpanded(expanded)
  }, [id, treeState, subtasks])

  // Fetch subtasks of this task
  React.useEffect(() => {
    if (loading || !task || Array.isArray(subtaskIDs)) {
      return
    }

    const loadSubtasks = async () => {
      setLoading(true)
      const ids = await getSubtasks(id, customFieldsToInclude)
      if (Array.isArray(ids)) {
        setSubtaskIDs(ids)
        setLoading(false)
      }
    }
    loadSubtasks()
  }, [task, id, loading, subtaskIDs, getSubtasks, customFieldsToInclude])

  const onSubMove = React.useCallback(
    (id: string) => {
      pushFrame(subtaskIDs, id)
    },
    [pushFrame, subtaskIDs]
  )

  // Moves a task from one index to another
  const handleMove = React.useCallback(
    (sourceID: string, targetID: string, after: boolean) => {
      // Add gaps to the array
      const expanded = nullExpandArray(subtasks)

      // Find indices of both tasks in newly expanded array
      const sourceIdx = expanded.findIndex(t => t?.id === sourceID)
      const targetIdx = expanded.findIndex(t => t?.id === targetID)

      // Perform move
      expanded[targetIdx + (after ? 1 : -1)] = expanded[sourceIdx]
      expanded[sourceIdx] = null

      // Remove gaps after change
      const moved = expanded.filter(Boolean)

      // Assign new values for sort order
      const toUpdate: Pick<APITask, 'id' | 'sort_order'>[] = moved.map(
        (task, i) => ({
          id: task.id,
          sort_order: i * 10
        })
      )

      // Update in api
      patchTasks(toUpdate)
        .then(() => dispatch(AddSuccessAlert('Changes saved', 1500)))
        .catch(err => dispatch(AddErrorAlert({ err })))
    },
    [subtasks, patchTasks, dispatch]
  )

  if (!task || hideSubs) {
    return null
  }

  if (loading) {
    return (
      <tr className={classes.spacer}>
        <td colSpan={row.getVisibleCells().length}>
          <CircularProgress className={classes.loading} />
        </td>
      </tr>
    )
  }

  if (subtaskIDs?.length === 0) {
    return null
  }

  return (
    <>
      {subtasks.map((subtask, i) => (
        <React.Fragment key={`${subtask.id}-${i}`}>
          <SingleSubtask
            row={row}
            index={i}
            onDragDrop={handleMove}
            dragDropID={id ?? row.original.id}
            task={subtask}
            targetRef={targetRef}
            onMove={onMove}
            expanded={expanded}
            setExpanded={setExpanded}
            smallMedia={smallMedia}
            depth={depth}
            onSubMove={onSubMove}
            observerId={observerId}
            enableCustomContextMenu={enableCustomContextMenu}
          />
          {/* Show more subtasks recursively */}
          {expanded.includes(subtask.id) && (
            <SubTask
              id={subtask.id}
              row={row}
              depth={depth + 1}
              targetRef={targetRef}
              smallMedia={smallMedia}
              onMove={onSubMove}
              observerId={observerId}
              customFieldsToInclude={customFieldsToInclude}
            />
          )}
        </React.Fragment>
      ))}
    </>
  )
}

export default React.memo(SubTask)
